import React, { useCallback, useEffect, useState, useMemo } from "react";
import classNames from "classnames";

import {
	Modal,
	Button
} from "rhombus-react";

import { useDispatch, useSelector } from "react-redux";
import { reset } from "redux-form";
import { useFlag } from '@innovid/feature-flags-react';

import { FEATURE_FLAGS } from 'featureFlag/featureFlag';
import { fetchSingleVersion } from "reducers/CreativeVersions";
import { changeTab } from "reducers/Social";
import { loadVersionDetails } from "reducers/VersionDetails";

import { PreviewVersion } from "utils/Preview";
import { API_URL, SOCIAL_AD_TYPES, VERSION_STATUS } from "utils/constants";
import {
	isSocialCreative,
	isAfterEffectsCreative,
	isImposiumCreative,
	isDynamicRenderSocial
} from "utils/common";

import { LABEL_MANAGEMENT } from "widgets/forms/list";
import {
	getSocialValuesFromVariables,
	setDynamicRenderUrls
} from "widgets/social2/utils";
import ProgressContainer from "widgets/ProgressContainer";
import VersionPreviewBladeToolbar from "widgets/modals/versions/VersionPreviewBladeToolbar";
import LabelsPanel from "widgets/modals/creatives/preview/LabelsPanel";
import PredictiveScorePanel from "widgets/modals/creatives/preview/PredictiveScorePanel";
import { usePredictiveScoringEnabled } from "hooks/usePredictiveScoringEnabled";
import useQueryParams from "hooks/useQueryParams";

import useFetchFallback from "hooks/useFetchFallback";
import { useReplayPreviewOnRenderNotify } from "hooks/useReplayPreviewOnRenderNotify";

import PreviewVersionBladeContent from "./PreviewVersionBladeContent";
import { FACEBOOK_TAB } from "../creatives/social/Tabs";

import "./PreviewVersionBlade.css";

const fbMOTypes = [
	SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO,
	SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO,
	SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO
];

export default function PreviewVersionBlade(props){
	const dispatch = useDispatch();
	const forceNewPreview = useFlag(FEATURE_FLAGS.NEW_PREVIEW_LINKS);
	let { onClose, creativeId, libraryId, versionId } = props
		, [isFullScreen, setIsFullScreen] = useState(false)
		, [versionInfo, setVersionInfo] = useState()
		, [randomKey, setRandomKey] = useState(0)
		, [isMuted, setIsMuted] = useState(true)
		, {
			isLoaded,
			versionDetails,
			selectedTab,
			aspectRatio,
			placement,
			creativeDetails,
		} = useSelector((state)=>{
			return {
				isLoaded:state.creativeVersions.isLoaded,
				versionDetails:state.versionDetails,
				selectedTab:state.social.selectedTab.value,
				aspectRatio:state.social.selectedAspectRatio,
				placement:state.social.facebookPlacementType.value,
				creativeDetails:state.creativeDetails,
			};
		})
		, title =  versionInfo?.name || "Loading"
		;

	const isPredictiveScoringOn = usePredictiveScoringEnabled();
	const { getParam, setParam } = useQueryParams();
	// Panel is hidden by default; shown only when the toolbar's predictive-score icon sets showScore=true.
	const showScore = getParam("showScore") === "true";

	const selection = useFetchFallback({
		selector: state => state.creativeVersions.items?.find(item => item.id === parseInt(versionId)),
		fetcher: () => dispatch(fetchSingleVersion(libraryId, creativeId, versionId)),
		shouldFetch: isLoaded,
	});

	const { width, height, socialValues = {}, adType, assetType, approval } = creativeDetails || {};

	const isSocial = isSocialCreative(creativeDetails);

	const onReplay = useCallback(() => {
		setRandomKey(previousKey => previousKey + 1);
	}, []);

	useReplayPreviewOnRenderNotify({
		creativeId,
		onReplay,
		enabled: isImposiumCreative(creativeDetails) || isAfterEffectsCreative(creativeDetails),
	});

	const iframeUrl = PreviewVersion.basic(creativeDetails, selection?.name, forceNewPreview).url;
	const { url: previewURL, isLegacy } = PreviewVersion.private(creativeDetails, selection?.name, forceNewPreview);

	const isTypeFBMO = fbMOTypes.includes(adType);

	useEffect(() => {
		if(isSocial) {
			dispatch(loadVersionDetails(libraryId, creativeId, versionId));
		}
	}, [creativeId, versionId, libraryId, isSocial]);

	let flattenedSocialVals = useMemo(() => {
		if(!isSocial){ return; }

		let versionDetailItems = {};
		versionDetails?.items.forEach((item) => {
			versionDetailItems = {
				...versionDetailItems,
				[item.name]:item,
			};
		});

		return getSocialValuesFromVariables(socialValues, versionDetailItems, libraryId, adType);
	}, [versionDetails, socialValues, libraryId, adType]);


	const isCarouselVideo = useMemo(() => {
		if (adType !== SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO) {
			return false;
		}

		const cards = flattenedSocialVals?.cards || [];
		return cards.some(card => card.type === "video");
	}, [flattenedSocialVals, adType]);

	const selectedVersion = selection && selection.status === VERSION_STATUS.LIVE ? selection : null;

	let adjustedSocialVals = useMemo(() => {
		if(isDynamicRenderSocial(creativeDetails)) {
			// dynamic imposium and aftereffects social version assets always need to be swapped
			// for imposium and ae social, asset fields will not be marked as dynamic, even though they are
			// randomKey is used as a cachebuster on the asset src, since the asset url doesn't change on imposium re-render
			return setDynamicRenderUrls(creativeDetails, versionId, flattenedSocialVals, randomKey || undefined);
		}
		return flattenedSocialVals;
	}, [
		flattenedSocialVals,
		assetType,
		approval,
		selectedVersion?.status,
		creativeDetails,
		libraryId,
		creativeId,
		versionId,
		randomKey
	]);

	let previewProps = useMemo(() => {
		return {
			socialValues: { ...adjustedSocialVals },
			baseDomain: API_URL,
			adType,
			isLoaded,
			partner: selectedTab,
			aspectRatio,
			placement,
			// eslint-disable-next-line
			fbPreviewVersion: isTypeFBMO ? 2 : 1,
			muted: isMuted,
		};
	}, [
		adjustedSocialVals,
		adType,
		isLoaded,
		selectedTab,
		aspectRatio,
		placement,
		isTypeFBMO,
		isMuted,
	]);

	const versionBladeContentProps = {
		isSocial,
		width,
		height,
		iframeUrl: isSocial ? null : iframeUrl,
		isTypeFBMO,
		previewProps,
	};

	useEffect(() => {
		if(selection){
			setVersionInfo(selection);
		}
	}, [selection]);

	function closeModal(){
		onClose();
		dispatch(reset(LABEL_MANAGEMENT)); /*
			We don't destroy the label management form on unmount, so we have to reset it manually
			when the blade closes.
		*/
	}

	function onToggleFullScreen(){
		setIsFullScreen(!isFullScreen);
	}

	function Footer(){
		return [
			<Button key="close" theme="default" onClick={closeModal} className="close-btn">Close</Button>,
		];
	}

	useEffect(() => {
		if(isTypeFBMO) {
			//set initial placement tab to facebook if adtype FBMO
			dispatch(changeTab(FACEBOOK_TAB));
		}
	}, [adType]);

	let versionBladeContentKey = versionBladeContentProps.previewProps.socialValues?.video || versionBladeContentProps.previewProps.socialValues?.image;

	function Content(){
		const isContentLoading = !isLoaded || (!versionDetails.isLoaded && isSocial)
			, contentClasses = {
				"main-content":true,
				"preview-version":true,
				"is-fb":isTypeFBMO,
				"is-PRVW":!isLegacy,
			}
			;

		return (
			<div className="main-content-container">
				<ProgressContainer
					className="preview-container"
					isLoading={isContentLoading}
				>
					<div className={classNames(contentClasses)}>
						<PreviewVersionBladeContent
							key={versionBladeContentKey?.id + randomKey}
							keyId={randomKey}
							{...versionBladeContentProps}
							isLegacy={isLegacy}
						/>
					</div>
				</ProgressContainer>
				<LabelsPanel
					creativeId={creativeId}
					versionId={versionId}
					versionDetails={versionInfo}
				/>
				{isPredictiveScoringOn && showScore && (
					<PredictiveScorePanel
						entityType="VERSION"
						entityId={versionId}
						libraryId={libraryId}
						onClose={() => setParam("showScore", "false", true)}
					/>
				)}
				<VersionPreviewBladeToolbar
					previewURL={previewURL}
					setIsMuted={setIsMuted}
					isCarouselVideo={isCarouselVideo}
					onReplay={onReplay}
					muted={isMuted}
					adType={adType}
				/>
			</div>
		);
	}

	let modalProps = {
		title,
		type: "blade",
		className: "preview-version-blade expandable",
		setAppTitle:false,

		xClose: true,
		allowFullScreen: true,
		isFullScreen,
		onToggleFullScreen,

		renderFooter: Footer,
		renderContent: Content,

		onClose: closeModal,
	};

	return <Modal {...modalProps} />;
}
